import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { API_URL } from "../config/API_URLS.tsx";
import { authenticatedFetch } from "../auth/RequestSigner.tsx";
import { RequestProcessModal } from "./RequestProcessModal";
import { ImgModal } from "./ImgModal";

export function AprendizDetails() {
    const [loading, setLoading] = useState(false);
    const [aprendizData, setAprendizData] = useState(null);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showModalImg, setShowModalImg] = useState(false);
    const [selectedPhotoUrl, setSelectedPhotoUrl] = useState(null);
    const { documentId } = useParams();
    const roll = parseInt(localStorage.getItem('userRoll'));
    
    // Obtener el estado de navegación que contiene los datos del aprendiz
    const location = useLocation();
    const aprendizPassedData = location.state?.aprendizData || null;
    
    // Obtener el tipo de solicitud de los query params
    // const queryParams = new URLSearchParams(location.search);
    // const requestType = parseInt(queryParams.get('requestType')) || 1; // Por defecto, pendiente

    const fetchAprendizDetails = useCallback(async () => {
        // Si ya tenemos los datos en el estado de navegación, no hacemos el fetch
        if (aprendizPassedData) {
            // Adaptar los datos para que coincidan con la estructura esperada
            const adaptedData = {
                ...aprendizPassedData,
                state_id: aprendizPassedData.status_id,
                vehicle_0: aprendizPassedData.vehicle[0] || {}
            };
            setAprendizData(adaptedData);
            return;
        }

        try {
            setLoading(true);
            const response = await authenticatedFetch(`${API_URL}/v1/aprendiz-statu/${documentId}`, {
                method: 'get',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (!response.ok) {
                throw new Error('Error al cargar los detalles del aprendiz');
            }
            
            const data = await response.json();
            setAprendizData(data);
            setLoading(false);
        } catch (error) {
            console.error('Error:', error);
            setError(error.message);
            setLoading(false);
        }
    }, [documentId, aprendizPassedData]);

    useEffect(() => {
        fetchAprendizDetails();
    }, [fetchAprendizDetails]);

    // Formatear fecha en formato DD/MM/YYYY
    function formatDate(dateString) {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0'); 
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    }

    // Etiqueta de estado según state_id
    const getStatusLabel = (stateId) => {
        switch(stateId) {
            case 4:
                return "Pendiente";
            case 5:
                return "Aceptado";
            case 6:
                return "Rechazado";
            default:
                return "Creado";
        }
    };

    // Color de estado según state_id
    const getStatusColor = (stateId) => {
        switch(stateId) {
            case 4:
                return "bg-orange-100 text-orange-800";
            case 5:
                return "bg-green-100 text-green-800";
            case 6:
                return "bg-red-100 text-red-800";
            default:
                return "bg-gray-100 text-gray-800";
        }
    };

    const handleOpenModal = () => {
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        // Refresh de la página al cerrar el modal para actualizar el estado del aprendiz
        // window.location.reload();
    };

    const handleOpenModalImg = (photoUrl) => {
        setShowModalImg(!showModalImg);
        setSelectedPhotoUrl(photoUrl);
    };

    return (
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto py-8" data-testid="aprendiz-details-container">
            {loading && (
                <div className="flex items-center justify-center p-8 min-h-[300px]" data-testid="aprendiz-details-loading">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-700"></div>
                    <span className="ml-3 text-gray-700">Cargando información del aprendiz...</span>
                </div>
            )}

            {!loading && aprendizData && (
                <div className="flex flex-col gap-6" data-testid="aprendiz-details-wrapper">
                    <div className="flex justify-between items-center mb-2">
                        <h1 className="text-2xl font-bold text-gray-800" data-testid="aprendiz-details-page-title">
                            Detalle de Solicitud
                        </h1>
                        <Link 
                            to="/user-registration-request" 
                            className="px-4 py-2 text-gray-900 text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group"
                            data-testid="aprendiz-details-back-button"
                        >
                            <div className="absolute inset-0 bg-gradient-to-t from-amber-300 to-amber-700 transition-transform duration-700 ease-in-out"></div>
                            <div className="absolute inset-0 bg-gradient-to-b from-amber-300 to-amber-700 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out"></div>
                            <span className="z-10 text-gray-900">Volver a la lista</span>
                        </Link>
                    </div>

                    <div className="bg-white rounded-lg shadow-md overflow-hidden" data-testid="aprendiz-details-content">
                        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-amber-50 to-amber-100">
                            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                                <div data-testid="aprendiz-header-info">
                                    <h2 className="text-xl font-bold text-gray-800" data-testid="aprendiz-name">
                                        {aprendizData.name} {aprendizData.last_name}
                                    </h2>
                                    <p className="text-gray-600 text-sm mt-1" data-testid="aprendiz-document">
                                        Documento: {aprendizData.document}
                                    </p>
                                </div>
                                <span 
                                    className={`px-3 py-1 inline-flex text-sm leading-5 font-semibold rounded-full ${getStatusColor(aprendizData.state_id)}`}
                                    data-testid="aprendiz-status-badge"
                                >
                                    {getStatusLabel(aprendizData.state_id)}
                                </span>
                            </div>
                        </div>

                        <div className="p-6">
                            <div className="grid md:grid-cols-2 gap-8">
                                <div className="space-y-6" data-testid="aprendiz-personal-info">
                                    <div className="mb-6">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-2" data-testid="aprendiz-personal-info-title">
                                            Información Personal
                                        </h3>
                                        <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm" data-testid="aprendiz-personal-info-list">
                                            <div>
                                                <span className="text-gray-500">Ficha:</span>
                                                <p className="text-gray-800 font-medium" data-testid="aprendiz-ficha-value">
                                                    {aprendizData.ficha}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Documento:</span>
                                                <p className="text-gray-800 font-medium" data-testid="aprendiz-document-value">
                                                    {aprendizData.document}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Fecha de registro:</span>
                                                <p className="text-gray-800 font-medium" data-testid="aprendiz-registry-date-value">
                                                    {formatDate(aprendizData.registry_date)}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Fecha de finalización:</span>
                                                <p className="text-gray-800 font-medium" data-testid="aprendiz-finish-date-value">
                                                    {formatDate(aprendizData.finish_date)}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Email:</span>
                                                <p className="text-gray-800 font-medium" data-testid="aprendiz-email-value">
                                                    {aprendizData.email || 'No disponible'}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Estado:</span>
                                                <p className={`font-medium ${
                                                    aprendizData.state_id === 4 ? 'text-orange-600' : 
                                                    aprendizData.state_id === 5 ? 'text-green-600' :
                                                    aprendizData.state_id === 6 ? 'text-red-600' : 'gray-800'
                                                }`} data-testid="aprendiz-status-value">
                                                    {getStatusLabel(aprendizData.state_id)}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-6">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-2" data-testid="aprendiz-vehicle-info-title">
                                            Información del Vehículo
                                        </h3>
                                        <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm" data-testid="aprendiz-vehicle-info-list">
                                            <div>
                                                <span className="text-gray-500">Marca:</span>
                                                <p className="text-gray-800 font-medium" data-testid="aprendiz-vehicle-marca-value">
                                                    {aprendizData.vehicle_0.marca}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Modelo:</span>
                                                <p className="text-gray-800 font-medium" data-testid="aprendiz-vehicle-modelo-value">
                                                    {aprendizData.vehicle_0.modelo}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Placa:</span>
                                                <p className="text-gray-800 font-medium" data-testid="aprendiz-vehicle-placa-value">
                                                    {aprendizData.vehicle_0.placa || (aprendizData.vehicle_0.numero_marco ? aprendizData.vehicle_0.numero_marco : 'No disponible')}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-gray-500">Color:</span>
                                                <p className="text-gray-800 font-medium" data-testid="aprendiz-vehicle-color-value">
                                                    {aprendizData.vehicle_0.color}
                                                </p>
                                            </div>
                                            <div className="col-span-2">
                                                <span className="text-gray-500">Observaciones:</span>
                                                <p className="text-gray-800 font-medium" data-testid="aprendiz-vehicle-observaciones-value">
                                                    {aprendizData.vehicle_0.observaciones || 'Sin observaciones'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-6" data-testid="aprendiz-images-and-actions">
                                    <div className="mb-4" data-testid="aprendiz-photo-container">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-2" data-testid="aprendiz-photo-title">
                                            Foto del Aprendiz
                                        </h3>
                                        <div className="bg-gray-100 rounded-lg overflow-hidden">
                                            <img 
                                                className="w-full h-auto object-contain max-h-64 mx-auto" 
                                                alt="Foto del aprendiz" 
                                                src={aprendizData.photo} 
                                                data-testid="aprendiz-photo"
                                                onClick={() => handleOpenModalImg(aprendizData.photo)}
                                                style={{cursor: 'pointer'}}
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-4" data-testid="vehicle-photo-container">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-2" data-testid="vehicle-photo-title">
                                            Foto del Vehículo
                                        </h3>
                                        <div className="bg-gray-100 rounded-lg overflow-hidden">
                                            <img 
                                                className="w-full h-auto object-contain max-h-64 mx-auto" 
                                                alt="Foto del vehículo" 
                                                src={aprendizData.vehicle_0.foto} 
                                                data-testid="vehicle-photo"
                                                onClick={() => handleOpenModalImg(aprendizData.vehicle_0.foto)}
                                                style={{cursor: 'pointer'}}
                                            />
                                        </div>
                                    </div>

                                    <div className="mb-4" data-testid="vehicle-document-container">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-2 border-b border-gray-200 pb-2" data-testid="vehicle-document-title">
                                            Tarjeta de Propiedad
                                        </h3>
                                        <div className="bg-gray-100 rounded-lg overflow-hidden">
                                            <img 
                                                className="w-full h-auto object-contain max-h-64 mx-auto" 
                                                alt="Tarjeta de propiedad" 
                                                src={aprendizData.vehicle_0.tarjeta_propiedad} 
                                                data-testid="vehicle-document-photo"
                                                onClick={() => handleOpenModalImg(aprendizData.vehicle_0.tarjeta_propiedad)}
                                                style={{cursor: 'pointer'}}
                                            />
                                        </div>
                                    </div>

                                    {roll === 1 && aprendizData.state_id === 4 && (
                                        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200" data-testid="aprendiz-actions-container">
                                            <h3 className="text-lg font-semibold text-gray-800 mb-3" data-testid="aprendiz-actions-title">
                                                Acciones
                                            </h3>
                                            
                                            <button
                                                onClick={handleOpenModal}
                                                className="w-full px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-md flex items-center justify-center relative overflow-hidden group"
                                                data-testid="aprendiz-process-button"
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-t from-amber-300 to-amber-700 transition-transform duration-700 ease-in-out"></div>
                                                <div className="absolute inset-0 bg-gradient-to-b from-amber-300 to-amber-700 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out"></div>
                                                <span className="z-10">Procesar Solicitud</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {showModalImg && <ImgModal img={selectedPhotoUrl} />}
                    {showModal && <RequestProcessModal
                        aprendizId={aprendizData.id}
                        isOpen={showModal} 
                        onClose={handleCloseModal} 
                    />}
                </div>
            )}

            {!loading && error && (
                <div className="bg-white rounded-lg shadow-md p-6 text-center" data-testid="aprendiz-details-error">
                    <div className="text-amber-600 text-5xl mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 9 0 11-18 0 9 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-800 mb-2">No se pudo cargar la información del aprendiz</h2>
                    <p className="text-gray-600 mb-6">Lo sentimos, pero no hemos podido encontrar la información solicitada.</p>
                    <Link 
                        to="/user-registration-request" 
                        className="px-4 py-2 text-gray-900 text-sm font-semibold rounded-lg shadow-md inline-flex items-center justify-center relative overflow-hidden group"
                    >
                        <div className="absolute inset-0 bg-gradient-to-t from-amber-300 to-amber-700 transition-transform duration-700 ease-in-out"></div>
                        <div className="absolute inset-0 bg-gradient-to-b from-amber-300 to-amber-700 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-in-out"></div>
                        <span className="z-10 text-gray-900">Volver a la lista de solicitudes</span>
                    </Link>
                </div>
            )}
        </div>
    );
}